import {
    ChangeDetectionStrategy,
    Component,
    forwardRef,
    input,
    output,
    signal
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import {
    ANC_RADIO_GROUP,
    type AncRadioGroupHost
} from './anc-radio-group.token';

let nextRadioGroupId = 0;

/** Visual layout for `lib-anc-radio-group`. */
export type AncRadioGroupVariant = 'default' | 'segmented';

/**
 * Design-system radio group (Figma node 190:6183).
 * Implements ControlValueAccessor — wrap `lib-anc-radio` children for reactive
 * or template-driven forms.
 *
 * @example Default
 * <lib-anc-radio-group formControlName="orderType">
 *   <lib-anc-radio [value]="'shipment'" [label]="'Shipment'"></lib-anc-radio>
 *   <lib-anc-radio [value]="'fulfilment'" [label]="'Fulfilment'"></lib-anc-radio>
 * </lib-anc-radio-group>
 *
 * @example Segmented pill toggle
 * <lib-anc-radio-group variant="segmented" name="orderType" [(ngModel)]="orderType">
 *   <lib-anc-radio [value]="'b2b'" [label]="'B2B Order'"></lib-anc-radio>
 *   <lib-anc-radio [value]="'b2c'" [label]="'B2C Order'"></lib-anc-radio>
 * </lib-anc-radio-group>
 */
@Component({
    selector: 'lib-anc-radio-group',
    imports: [CommonModule],
    templateUrl: './anc-radio-group.component.html',
    styleUrls: ['./anc-radio-group.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => AncRadioGroupComponent),
            multi: true
        },
        {
            provide: ANC_RADIO_GROUP,
            useExisting: AncRadioGroupComponent
        }
    ]
})
export class AncRadioGroupComponent
    implements ControlValueAccessor, AncRadioGroupHost
{
    private readonly defaultName = `anc-radio-group-${nextRadioGroupId++}`;

    /** Shared `name` for native radio inputs (auto-generated per instance when omitted). */
    name = input<string>();

    /** Layout direction for the radio options. */
    orientation = input<'vertical' | 'horizontal'>('vertical');

    /**
     * Visual style — `segmented` renders a horizontal pill toggle (label-only
     * options, no radio circles). Implies horizontal layout.
     */
    variant = input<AncRadioGroupVariant>('default');

    /** Emits when the selected value changes. */
    readonly changed = output<string | number | boolean>();

    /** Currently selected value, driven by the form control or user selection. */
    readonly value = signal<string | number | boolean | null>(null);
    /** Group-level disabled state, driven by the form control. */
    readonly disabled = signal<boolean>(false);

    private onChange: (value: string | number | boolean | null) => void =
        () => {};
    private onTouched: () => void = () => {};

    /** Resolved `name` attribute for child native radio inputs. */
    resolvedName(): string {
        return this.name() ?? this.defaultName;
    }

    /** Select a value from a child radio button. */
    select(optionValue: string | number | boolean): void {
        if (this.disabled()) {
            return;
        }

        this.value.set(optionValue);
        this.onChange(optionValue);
        this.onTouched();
        this.changed.emit(optionValue);
    }

    // --- ControlValueAccessor ---
    writeValue(value: string | number | boolean | null): void {
        this.value.set(value);
    }

    registerOnChange(
        fn: (value: string | number | boolean | null) => void
    ): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        // Defer signal write — FormControl.disable() can run during change detection (NG0600).
        queueMicrotask(() => this.disabled.set(isDisabled));
    }
}
